/**
 * CRM API service
 * Clients, communications, tasks, and leads
 */
import api, { getErrorMessage } from './api'
import type {
  Client,
  ClientLicense,
  ClientNote,
  ClientCreate,
  ClientUpdate,
  ClientListResponse,
  ClientMatchResult,
  Communication,
  CommunicationCreate,
  CommunicationUpdate,
  CommunicationListResponse,
  Task,
  TaskCreate,
  TaskUpdate,
  TaskListResponse,
  LeadListResponse,
  NotificationLog,
  DashboardStats,
  DncStatus,
  DoNotContactEntry,
  DoNotContactListResponse,
  DncMatchType,
  DncSortKey,
  BulkResult,
  BusinessCardTemplate,
  BusinessCardSendRequest,
  BusinessCardSendResponse,
  ComposableTemplate,
  ScheduledMessage,
  SendMessageNowRequest,
  ScheduleMessageRequest,
  MessagePreviewResponse,
  FCCEntity,
} from '../types'

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export async function listClients(params?: {
  account_status?: string
  priority?: string
  assigned_to_user_id?: number
  my_clients?: boolean
  unassigned?: boolean
  /** Two-letter code. Matches the LICENSEE address on a linked license, not the agent's. */
  state?: string
  /** 1–365. Holds a license expiring between today and N days out; not already expired. */
  expiring_within_days?: number
  has_open_tasks?: boolean
  /** Holds a license whose FCC entity name or FRN changed and nobody has reviewed it. */
  has_ownership_change?: boolean
  /** Nothing live: no license in force or recently lapsed, no contact, no task, no queued mail. */
  stale?: boolean
  search?: string
  /**
   * Server-side sort. Must be a key of `_CLIENT_SORTS` in backend/app/api/v1/crm.py —
   * anything else is a 422. `priority` orders by severity, not alphabetically. Last
   * Contact, Tasks and Licenses are computed after paging and are deliberately absent.
   */
  sort?: 'entity_name' | 'account_status' | 'priority'
  order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}): Promise<ClientListResponse> {
  try {
    const response = await api.get<ClientListResponse>('/v1/crm/clients', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createClient(data: ClientCreate): Promise<Client> {
  try {
    const response = await api.post<Client>('/v1/crm/clients', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getClient(id: number): Promise<Client> {
  try {
    const response = await api.get<Client>(`/v1/crm/clients/${id}`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateClient(id: number, data: ClientUpdate): Promise<Client> {
  try {
    const response = await api.patch<Client>(`/v1/crm/clients/${id}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteClient(id: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/clients/${id}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function matchClients(params: {
  entity_name?: string
  frn?: string
  phone?: string
}): Promise<ClientMatchResult[]> {
  try {
    const response = await api.get<ClientMatchResult[]>('/v1/crm/clients/match', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function linkLicense(clientId: number, fcc_unique_system_identifier: number): Promise<ClientLicense> {
  try {
    const response = await api.post<ClientLicense>(`/v1/crm/clients/${clientId}/licenses`, {
      fcc_unique_system_identifier,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateClientLicense(
  licenseId: number,
  patch: { notify_enabled?: boolean; do_not_renew?: boolean },
): Promise<ClientLicense> {
  try {
    const response = await api.patch<ClientLicense>(`/v1/crm/client-licenses/${licenseId}`, patch)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function unlinkClientLicense(licenseId: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/client-licenses/${licenseId}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Communications
// ---------------------------------------------------------------------------

export async function listCommunications(
  clientId: number,
  params?: { page?: number; page_size?: number }
): Promise<CommunicationListResponse> {
  try {
    const response = await api.get<CommunicationListResponse>(
      `/v1/crm/clients/${clientId}/communications`,
      { params }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createCommunication(
  clientId: number,
  data: CommunicationCreate
): Promise<Communication> {
  try {
    const response = await api.post<Communication>(
      `/v1/crm/clients/${clientId}/communications`,
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateCommunication(
  id: number,
  data: CommunicationUpdate
): Promise<Communication> {
  try {
    const response = await api.patch<Communication>(`/v1/crm/communications/${id}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteCommunication(id: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/communications/${id}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export async function listTasks(params?: {
  my_tasks?: boolean
  unassigned?: boolean
  assigned_to_user_id?: number
  client_id?: number
  lead_id?: number
  status?: string
  priority?: string
  /** 1–365. Due between now and N days out — overdue tasks are NOT included. */
  due_within_days?: number
  include_completed?: boolean
  overdue_only?: boolean
  show_deleted?: boolean
  /** Free text over title, description, and the linked client/lead name. */
  search?: string
  /**
   * Server-side sort. Must be one of `_TASK_SORTS` in backend/app/api/v1/crm.py — anything
   * else is a 422. `priority` orders by severity, not alphabetically.
   */
  sort?: 'title' | 'status' | 'priority' | 'due' | 'created'
  order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}): Promise<TaskListResponse> {
  try {
    const response = await api.get<TaskListResponse>('/v1/crm/tasks', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createTask(data: TaskCreate): Promise<Task> {
  try {
    const response = await api.post<Task>('/v1/crm/tasks', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateTask(id: number, data: TaskUpdate): Promise<Task> {
  try {
    const response = await api.patch<Task>(`/v1/crm/tasks/${id}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteTask(id: number, permanent?: boolean): Promise<void> {
  try {
    await api.delete(`/v1/crm/tasks/${id}`, { params: permanent ? { permanent: true } : undefined })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    const response = await api.get<DashboardStats>('/v1/crm/dashboard')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export interface LicenseCrmStatus {
  is_lead: boolean
  lead_id: number | null
  is_client: boolean
  client_id: number | null
}

export async function getLicenseCrmStatus(usi: number): Promise<LicenseCrmStatus> {
  try {
    const response = await api.get<LicenseCrmStatus>(
      '/v1/crm/license-status',
      { params: { usi } }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export async function listLeads(params?: {
  days?: number
  /** Set false to see only the built-in expiring-soon rule, without saved-search feeds. */
  include_saved_searches?: boolean
  /** Narrows the discovered leads by call sign / entity name. Never widens the feed. */
  search?: string
  /** Licensee's state. AND-ed with the feed's sources, so it can only narrow. */
  state?: string
  service_code?: string
  page?: number
  page_size?: number
}): Promise<LeadListResponse> {
  try {
    const response = await api.get<LeadListResponse>('/v1/crm/leads', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function convertLead(fcc_unique_system_identifier: number): Promise<Client> {
  try {
    const response = await api.post<Client>('/v1/crm/leads/convert', null, {
      params: { fcc_unique_system_identifier },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Tracked Leads
// ---------------------------------------------------------------------------

export interface TrackedLead {
  id: number
  fcc_unique_system_identifier: number
  call_sign: string | null
  entity_name: string | null
  source: 'manual' | 'bulk_search' | 'auto'
  search_description: string | null
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'open' | 'converted' | 'dismissed'
  /** How far along an open lead is — see LeadWorkState. Not its lifecycle. */
  work_state: LeadWorkState
  converted_to_client_id: number | null
  assigned_to_user_id: number | null
  assigned_to: { id: number; username: string | null; full_name: string | null; email: string } | null
  created_by_user_id: number | null
  created_at: string
  updated_at: string
  notes: TrackedLeadNote[]
  // Our own contact details, stored alongside the FCC's entity_* values below —
  // the FCC address is often out of date, and the two are shown side by side so
  // a disagreement is visible rather than flattened.
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  expired_date: string | null
  days_until_expiration: number | null
  radio_service_code: string | null
  license_status: string | null
  city: string | null
  state: string | null
  entity_email: string | null
  entity_phone: string | null
  entity_fax: string | null
  entity_first_name: string | null
  entity_last_name: string | null
  entity_frn: string | null
  entity_street_address: string | null
  entity_zip_code: string | null
  transmitter_address: string | null
  transmitter_city: string | null
  transmitter_county: string | null
  transmitter_state: string | null
}

export interface TrackedLeadNote {
  id: number
  lead_id: number
  note: string
  created_by_user_id: number | null
  created_by: { id: number; username: string | null; full_name: string | null; email: string } | null
  created_at: string
}

export interface TrackedLeadListResponse {
  total: number
  page: number
  page_size: number
  items: TrackedLead[]
}

export interface BulkLeadResult {
  added: number
  skipped: number
  errors: number
  lead_ids: number[]
}

export async function createTrackedLead(data: {
  fcc_unique_system_identifier: number
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  note?: string
  source?: 'manual' | 'bulk_search' | 'auto'
  search_description?: string
  assigned_to_user_id?: number
}): Promise<TrackedLead> {
  try {
    const response = await api.post<TrackedLead>('/v1/crm/leads', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function bulkCreateTrackedLeads(data: {
  fcc_usis: number[]
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  note?: string
  search_description?: string
}): Promise<BulkLeadResult> {
  try {
    const response = await api.post<BulkLeadResult>('/v1/crm/leads/bulk', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** The three states a tracked lead can be in. */
export type TrackedLeadStatus = 'open' | 'converted' | 'dismissed'

/**
 * How far along an **open** lead is — deliberately not its lifecycle.
 *
 * `TrackedLeadStatus` answers "is this lead still alive"; this answers "has
 * anyone actually done anything with it". An assignee says whose the lead is,
 * not how far along it is, so neither of the two existing fields covered this.
 *
 * Three values and no more: WRR's system was three colours, this is a shop of
 * fewer than five people, and a long stage list nobody maintains is worse than
 * no list at all.
 */
export type LeadWorkState = 'new' | 'working' | 'waiting'

/**
 * The work states in the order a lead moves through them, with the wording
 * shown on screen. One list, used by the Leads table, the lead page and the
 * filter bar, so the three cannot drift apart.
 */
export const LEAD_WORK_STATES: { value: LeadWorkState; label: string }[] = [
  { value: 'new', label: 'Not started' },
  { value: 'working', label: 'Working' },
  { value: 'waiting', label: 'Waiting on them' },
]

export async function listTrackedLeads(params?: {
  /**
   * Statuses to include. An array asks for several at once (all three is "every
   * lead"); a bare value is the older single-status form, still accepted. Omitted
   * means open leads only — the server's default, not a client-side one.
   */
  status?: TrackedLeadStatus | TrackedLeadStatus[]
  /**
   * How far along the leads are. Repeatable in the same way `status` is, so
   * `['new', 'waiting']` asks for everything nobody is actively pushing.
   * Omitted means every work state — unlike `status`, nothing is hidden by
   * default, because there is no historical scope to preserve.
   */
  work_state?: LeadWorkState | LeadWorkState[]
  priority?: 'low' | 'medium' | 'high' | 'urgent'
  assigned_to_user_id?: number
  my_leads?: boolean
  unassigned?: boolean
  /** Licensee's state (two-letter) and the license's FCC radio service. */
  state?: string
  service_code?: string
  /** Free text over call sign, entity name, and saved-search description. */
  search?: string
  /**
   * Server-side sort. Must be a key of `_TRACKED_LEAD_SORTS` in
   * backend/app/api/v1/crm.py — anything else is a 422. `priority` orders by severity,
   * not alphabetically, and is the default (descending) — a whole-list ordering as of
   * v0.41.7, where it used to be a re-sort of the fetched page. `expires` became
   * sortable in the same change, when the query gained a LEFT JOIN to fcc_licenses.
   */
  sort?: 'call_sign' | 'entity_name' | 'priority' | 'expires' | 'updated_at'
  order?: 'asc' | 'desc'
  page?: number
  page_size?: number
}): Promise<TrackedLeadListResponse> {
  try {
    // The endpoint takes `status` as a repeatable query param, so an array must
    // serialise as status=open&status=converted. Axios' default array format
    // appends `[]` to the name ("status[]="), which the server would not read as
    // `status` at all — it would silently fall back to the open-only default.
    const response = await api.get<TrackedLeadListResponse>('/v1/crm/leads/tracked', {
      params,
      paramsSerializer: { indexes: null },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateTrackedLead(
  leadId: number,
  // assigned_to_user_id: null unassigns; omit the key to leave the assignee alone.
  // Contact fields follow the same convention as assigned_to_user_id: send null
  // to clear the override and fall back to the FCC value, omit to leave alone.
  data: {
    priority?: string
    status?: string
    work_state?: LeadWorkState
    assigned_to_user_id?: number | null
    contact_name?: string | null
    contact_email?: string | null
    contact_phone?: string | null
  }
): Promise<TrackedLead> {
  try {
    const response = await api.patch<TrackedLead>(`/v1/crm/leads/tracked/${leadId}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteTrackedLead(leadId: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/leads/tracked/${leadId}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getTrackedLead(leadId: number): Promise<TrackedLead> {
  try {
    const response = await api.get<TrackedLead>(`/v1/crm/leads/tracked/${leadId}`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Client License Stats
// ---------------------------------------------------------------------------

export interface ClientLicenseStats {
  total_licenses: number
  managed_licenses: number
  expiring_90_days: number
  expired: number
}

export interface ExpiringSoonEntry {
  license_id: number
  call_sign: string | null
  expiration_date: string
}

export interface ClientLicenseStatsResponse {
  stats: ClientLicenseStats
  expiring_soon: ExpiringSoonEntry[]
  expired_license_ids: number[]
}

export async function getClientLicenseStats(clientId: number): Promise<ClientLicenseStatsResponse> {
  try {
    const response = await api.get<ClientLicenseStatsResponse>(
      `/v1/crm/clients/${clientId}/license-stats`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * One FCC party on a client's licenses, with the call signs it appears on.
 * De-duplicated server-side: a client with 40 licenses usually has one licensee
 * and one filing agent.
 */
export interface ClientFCCParty {
  entity: FCCEntity
  call_signs: string[]
}

export async function getClientFccParties(clientId: number): Promise<ClientFCCParty[]> {
  try {
    const response = await api.get<ClientFCCParty[]>(`/v1/crm/clients/${clientId}/fcc-parties`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function getLeadFccParties(leadId: number): Promise<ClientFCCParty[]> {
  try {
    const response = await api.get<ClientFCCParty[]>(
      `/v1/crm/leads/tracked/${leadId}/fcc-parties`
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createQuickTask(
  clientId: number,
  data: { call_sign: string; expiration_date: string; template: 'renew' | 'contact_first' }
): Promise<import('../types').Task> {
  try {
    const response = await api.post<import('../types').Task>(
      `/v1/crm/clients/${clientId}/quick-task`,
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Client Notes
// ---------------------------------------------------------------------------

export async function listClientNotes(clientId: number): Promise<ClientNote[]> {
  try {
    const response = await api.get<ClientNote[]>(`/v1/crm/clients/${clientId}/notes`)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function addClientNote(clientId: number, note: string): Promise<ClientNote> {
  try {
    const response = await api.post<ClientNote>(`/v1/crm/clients/${clientId}/notes`, { note })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteClientNote(clientId: number, noteId: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/clients/${clientId}/notes/${noteId}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function addLeadNote(leadId: number, note: string): Promise<TrackedLeadNote> {
  try {
    const response = await api.post<TrackedLeadNote>(`/v1/crm/leads/tracked/${leadId}/notes`, { note })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteLeadNote(leadId: number, noteId: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/leads/tracked/${leadId}/notes/${noteId}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export async function getClientNotifications(clientId: number, limit = 50): Promise<NotificationLog[]> {
  try {
    const response = await api.get<NotificationLog[]>(`/v1/crm/clients/${clientId}/notifications`, {
      params: { limit },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function sendCustomNotification(
  clientId: number,
  subject: string,
  message_body: string
): Promise<{ success: boolean; recipient: string }> {
  try {
    const response = await api.post<{ success: boolean; recipient: string }>(
      `/v1/crm/clients/${clientId}/notifications/send`,
      { subject, message_body }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Do Not Contact
// ---------------------------------------------------------------------------

export async function listDnc(params?: {
  /** Matches the suppressed value **or** the reason. */
  search?: string
  match_type?: DncMatchType
  sort?: DncSortKey
  order?: 'asc' | 'desc'
  /**
   * Also return entries that have been lifted, carrying `deleted_at`,
   * `deleted_by_user_id` and `removal_reason` — the history view.
   *
   * Removals used to vanish from this page entirely, so the one event most worth
   * keeping on a suppression list was the one you could not look up.
   */
  include_removed?: boolean
  page?: number
  page_size?: number
}): Promise<DoNotContactListResponse> {
  try {
    const response = await api.get<DoNotContactListResponse>('/v1/crm/do-not-contact', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createDnc(data: {
  match_type: string
  value: string
  reason?: string
}): Promise<DoNotContactEntry> {
  try {
    const response = await api.post<DoNotContactEntry>('/v1/crm/do-not-contact', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Edit an entry.
 *
 * `reason` is what this is for — an entry flagged from a search result carries none,
 * because the toggle does not collect one. Employees may set it.
 *
 * `value` and `match_type` are **admin only** server-side, because changing what an
 * entry matches silently un-suppresses whoever it used to match — the same capability
 * as deleting it. A refused edit writes nothing, not the permitted half.
 */
export async function updateDnc(
  id: number,
  data: { reason?: string; value?: string; match_type?: string },
): Promise<DoNotContactEntry> {
  try {
    const response = await api.patch<DoNotContactEntry>(
      `/v1/crm/do-not-contact/${id}`, data,
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Lift a suppression. The optional reason is recorded on the row, not just in audit. */
export async function deleteDnc(id: number, reason?: string): Promise<void> {
  try {
    await api.delete(`/v1/crm/do-not-contact/${id}`, {
      params: reason ? { reason } : undefined,
    })
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Remove several DNC entries at once (admin only).
 *
 * Ids only — the API has no filter mode here, deliberately. Removing a suppression
 * re-enables outreach to someone who asked not to be contacted, so "delete everything
 * matching this search" is not a trade worth making on a hand-curated list.
 */
export async function bulkDeleteDnc(ids: number[], reason?: string): Promise<BulkResult> {
  try {
    const response = await api.post<BulkResult>('/v1/crm/do-not-contact/bulk-delete', {
      mode: 'ids',
      ids,
      ...(reason ? { reason } : {}),
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function checkDnc(
  match_type: string,
  value: string
): Promise<{ is_dnc: boolean; entry: DoNotContactEntry | null }> {
  try {
    const response = await api.get<{ is_dnc: boolean; entry: DoNotContactEntry | null }>(
      '/v1/crm/do-not-contact/check',
      { params: { match_type, value } }
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function toggleDnc(data: {
  match_type: string
  value: string
  reason?: string
}): Promise<{ is_dnc: boolean; entry: DoNotContactEntry | null }> {
  try {
    const response = await api.post<{ is_dnc: boolean; entry: DoNotContactEntry | null }>(
      '/v1/crm/do-not-contact/toggle',
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

// ---------------------------------------------------------------------------
// Business Cards
// ---------------------------------------------------------------------------

export async function listBusinessCards(): Promise<BusinessCardTemplate[]> {
  try {
    const response = await api.get<BusinessCardTemplate[]>('/v1/crm/business-cards')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function listAllBusinessCards(): Promise<BusinessCardTemplate[]> {
  try {
    const response = await api.get<BusinessCardTemplate[]>('/v1/crm/business-cards/all')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function createBusinessCard(data: {
  name: string
  card_type: 'personal' | 'company'
  sender_name: string
  title?: string
  email?: string
  phone?: string
  greeting_text?: string
}): Promise<BusinessCardTemplate> {
  try {
    const response = await api.post<BusinessCardTemplate>('/v1/crm/business-cards', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function updateBusinessCard(
  id: number,
  data: Partial<{
    name: string
    sender_name: string
    title: string | null
    email: string | null
    phone: string | null
    greeting_text: string | null
    is_active: boolean
  }>
): Promise<BusinessCardTemplate> {
  try {
    const response = await api.patch<BusinessCardTemplate>(`/v1/crm/business-cards/${id}`, data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function deleteBusinessCard(id: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/business-cards/${id}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Is this client on the Do Not Contact list? Read before showing a send form, so
 * the sender is warned up front rather than stopped at the last moment.
 */
export async function getClientDncStatus(
  clientId: number,
  email?: string
): Promise<DncStatus> {
  try {
    const response = await api.get<DncStatus>(`/v1/crm/clients/${clientId}/dnc-status`, {
      params: email ? { email } : undefined,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** The lead twin of {@link getClientDncStatus}. */
export async function getLeadDncStatus(
  leadId: number,
  email?: string
): Promise<DncStatus> {
  try {
    const response = await api.get<DncStatus>(`/v1/crm/leads/tracked/${leadId}/dnc-status`, {
      params: email ? { email } : undefined,
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Is this bare address on the Do Not Contact list?
 *
 * The record-less twin of the two above, for the send flow that has no client or
 * lead behind it. Only `email` entries can answer — the other three match types
 * describe a license, and there is no license here.
 */
export async function getEmailDncStatus(email: string): Promise<DncStatus> {
  try {
    const response = await api.get<DncStatus>('/v1/crm/dnc-status/email', {
      params: { email },
    })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/**
 * Send a card to a plain e-mail address, with no client or lead involved.
 *
 * The address **is** checked against the suppression list, via the `email` match
 * type — pass `acknowledge_dnc` to send anyway after the warning. What still
 * cannot be checked here is a suppressed *call sign*: the license-shaped match
 * types need a record to resolve through, and there is none. Use the client or
 * lead call where a CRM record exists — those resolve all four.
 */
export async function sendBusinessCardToAddress(
  cardId: number,
  data: {
    to_email: string
    subject?: string
    personal_note?: string
    acknowledge_dnc?: boolean
  },
): Promise<BusinessCardSendResponse> {
  try {
    const response = await api.post<BusinessCardSendResponse>(
      `/v1/crm/business-cards/${cardId}/send`,
      data,
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function sendBusinessCardToLead(
  leadId: number,
  data: BusinessCardSendRequest
): Promise<BusinessCardSendResponse> {
  try {
    const response = await api.post<BusinessCardSendResponse>(
      `/v1/crm/leads/${leadId}/send-business-card`,
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function sendBusinessCardToClient(
  clientId: number,
  data: BusinessCardSendRequest
): Promise<BusinessCardSendResponse> {
  try {
    const response = await api.post<BusinessCardSendResponse>(
      `/v1/crm/clients/${clientId}/send-business-card`,
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}


// ---------------------------------------------------------------------------
// Messages — one composition, sent now or queued for later
// ---------------------------------------------------------------------------
//
// The two send paths differ in exactly one respect, and it is deliberate:
// sending now warns about Do Not Contact and lets a person override it, because
// a person is present. A scheduled message is checked again by the cron runner
// at fire time, where nobody is, and there it is blocked outright.

/** The messages available to send. Staff-readable, unlike the admin template list. */
export async function listComposableTemplates(): Promise<ComposableTemplate[]> {
  try {
    const response = await api.get<ComposableTemplate[]>('/v1/crm/message-templates')
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Render a message against the real record it is addressed to. Sends nothing. */
export async function previewMessage(
  data: SendMessageNowRequest
): Promise<MessagePreviewResponse> {
  try {
    const response = await api.post<MessagePreviewResponse>(
      '/v1/crm/scheduled-messages/preview',
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Send immediately. A 409 means the recipient is suppressed — retry with acknowledge_dnc. */
export async function sendMessageNow(
  data: SendMessageNowRequest
): Promise<BusinessCardSendResponse> {
  try {
    const response = await api.post<BusinessCardSendResponse>(
      '/v1/crm/scheduled-messages/send-now',
      data
    )
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Queue a message for a time the sender picked, in the sender's own zone. */
export async function scheduleMessage(
  data: ScheduleMessageRequest
): Promise<ScheduledMessage> {
  try {
    const response = await api.post<ScheduledMessage>('/v1/crm/scheduled-messages', data)
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

export async function listScheduledMessages(params?: {
  client_id?: number
  lead_id?: number
  include_finished?: boolean
  limit?: number
}): Promise<ScheduledMessage[]> {
  try {
    const response = await api.get<ScheduledMessage[]>('/v1/crm/scheduled-messages', { params })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Pause, resume or cancel. The words of a queued message are not editable. */
export async function updateScheduledMessage(
  id: number,
  status: 'scheduled' | 'paused' | 'cancelled'
): Promise<ScheduledMessage> {
  try {
    const response = await api.patch<ScheduledMessage>(`/v1/crm/scheduled-messages/${id}`, { status })
    return response.data
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}

/** Clear a finished message out of the Outbox. Cancel a waiting one instead. */
export async function deleteScheduledMessage(id: number): Promise<void> {
  try {
    await api.delete(`/v1/crm/scheduled-messages/${id}`)
  } catch (error) {
    throw new Error(getErrorMessage(error))
  }
}
